// MaturityCard — read-only family-maturity card for the Management View
// Overview tab (PRODOPSAI-403, parent epic PRODOPSAI-400 / Phase 2).
//
// SELF-CONTAINED: unlike Phase 1's `MatrixCard` (props-only), this card OWNS
// its data fetch via the existing `useFamilyMaturity` hook (Phase 0 US5) and
// its role gate via `useAuth`. The Overview tab mounts it without props.
//
// Renders the seven seeded family rows (already sorted by `displayOrder` on
// the server) with status pills whose colour mapping mirrors Phase 0 US5's
// admin editor (`FamilyMaturitySection`). DataQualityAdmin / SiteAdmin users
// also see an inline link to `/admin/metric-definitions`; for plain `User`
// the link is fully absent from the DOM (per AC).
//
// Visual rhythm matches `MatrixCard`: same `Card` / `CardHeader` /
// `CardContent` primitives, same title weight + leading.

import { Link } from 'react-router-dom';
import type { IFamilyMaturity, MaturityStatus } from '@skystone/shared';
import { Badge, Card, CardContent, CardHeader, ErrorBanner, Spinner } from '@/components/ui';
import { useFamilyMaturity } from '@/hooks/use-family-maturity';
import { useAuth } from '@/contexts/AuthContext';

// ---------------------------------------------------------------------------
// Module-scope helpers — defined outside the render body to satisfy SonarCloud
// rule S7721 (no helper components inside the render).
// ---------------------------------------------------------------------------

/**
 * Status → Badge variant mapping. Mirrors the mapping used by Phase 0 US5's
 * admin editor (`FamilyMaturitySection`):
 *
 *   - `Implemented` → primary navy (`default`)
 *   - `InProgress`  → warning amber (`warning`)
 *   - `NotStarted`  → error red (`error`)
 *   - `Prioritized` → muted grey (`secondary`)
 *
 * Visual consistency between the read-only Overview card and the editable
 * admin page is intentional — both render the same status with the same pill.
 */
const STATUS_TONE: Record<MaturityStatus, 'default' | 'warning' | 'error' | 'secondary'> = {
  Implemented: 'default',
  InProgress: 'warning',
  NotStarted: 'error',
  Prioritized: 'secondary',
};

/** Human-readable status labels (insert space in `InProgress` / `NotStarted`). */
const STATUS_LABEL: Record<MaturityStatus, string> = {
  Implemented: 'Implemented',
  InProgress: 'In Progress',
  NotStarted: 'Not Started',
  Prioritized: 'Prioritized',
};

interface MaturityRowProps {
  row: IFamilyMaturity;
}

/**
 * One row: family label on the left, status pill on the right. A subtle
 * bottom border separates rows (the last row's border is hidden via
 * `last:border-b-0` on the parent container).
 */
function MaturityRow({ row }: Readonly<MaturityRowProps>) {
  return (
    <div
      className="flex items-center justify-between gap-3 border-b border-border py-2 last:border-b-0"
      data-testid="maturity-row"
      data-family-code={row.familyCode}
    >
      <p className="text-sm font-medium text-foreground">{row.label}</p>
      <Badge variant={STATUS_TONE[row.status]}>{STATUS_LABEL[row.status]}</Badge>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------------

/**
 * Read-only card rendering the seven metric families and their current
 * maturity status. Self-contained (no props) — the Overview tab simply
 * mounts `<MaturityCard />` and the card pulls its own data + auth state.
 */
export function MaturityCard() {
  const { rows, isLoading, error } = useFamilyMaturity();
  const { user } = useAuth();
  const showAdminLink = user?.role === 'DataQualityAdmin' || user?.role === 'SiteAdmin';

  return (
    <Card data-testid="maturity-card">
      <CardHeader>
        <h3 className="text-sm font-bold leading-tight text-foreground">Metric maturity</h3>
      </CardHeader>
      <CardContent>
        {isLoading && (
          <div className="flex items-center justify-center py-6">
            <Spinner />
          </div>
        )}
        {error && <ErrorBanner message={error} />}
        {!isLoading && !error && rows.length === 0 && (
          <p className="py-6 text-center text-sm text-muted-foreground">
            No metric maturity defined.
          </p>
        )}
        {!isLoading && !error && rows.length > 0 && (
          <>
            <div className="space-y-0">
              {rows.map((row) => (
                <MaturityRow key={row.familyCode} row={row} />
              ))}
            </div>
            {showAdminLink && (
              <div className="mt-3">
                <Link
                  to="/admin/metric-definitions"
                  className="text-xs text-primary hover:underline"
                >
                  Manage in Data/Quality Admin
                </Link>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default MaturityCard;
